/**
 * Builds self-contained `@font-face` CSS (with base64-embedded font data) for
 * use inside exported SVG/PNG assets.
 *
 * Why this is needed: when an SVG is serialized, loaded into an <img> via a
 * blob: URL, and rasterized to a <canvas>, the browser renders it in an
 * isolated document context that has NO access to the parent page's loaded
 * fonts or @font-face rules. Referencing a font by name only (e.g.
 * font-family: 'Unica77 Cohere') therefore fails silently and the rasterizer
 * falls back to a system font — which is why the exported PNG does not match
 * the live preview. Embedding the actual font bytes as data: URIs makes the
 * exported SVG self-contained so it rasterizes with the correct glyphs.
 */

// Cache resolved data URIs (keyed by absolute font URL) across exports so we
// only fetch + base64-encode each font file once per session.
const dataUriCache = new Map();

function stripQuotes(value) {
  return String(value || '')
    .trim()
    .replace(/^['"]/, '')
    .replace(/['"]$/, '');
}

/** Returns the first concrete family name from a font stack, unquoted + trimmed. */
function firstFamilyToken(stack) {
  if (!stack) return '';
  return stripQuotes(String(stack).split(',')[0]);
}

function fontMimeForUrl(url) {
  if (/\.woff2(\?|$)/i.test(url)) return 'font/woff2';
  if (/\.woff(\?|$)/i.test(url)) return 'font/woff';
  if (/\.otf(\?|$)/i.test(url)) return 'font/otf';
  if (/\.ttf(\?|$)/i.test(url)) return 'font/ttf';
  return 'application/octet-stream';
}

function fontFormatForUrl(url) {
  if (/\.woff2(\?|$)/i.test(url)) return 'woff2';
  if (/\.woff(\?|$)/i.test(url)) return 'woff';
  if (/\.otf(\?|$)/i.test(url)) return 'opentype';
  return 'truetype';
}

/** Picks the best (woff2 > woff > first) url() from a CSS `src` declaration. */
function extractBestUrl(srcValue) {
  const urls = [];
  const re = /url\(\s*(['"]?)([^'")]+)\1\s*\)/g;
  let match;
  while ((match = re.exec(srcValue)) !== null) {
    urls.push(match[2]);
  }
  if (!urls.length) return null;
  return (
    urls.find((u) => /\.woff2(\?|$)/i.test(u)) ||
    urls.find((u) => /\.woff(\?|$)/i.test(u)) ||
    urls[0]
  );
}

async function fetchAsDataUri(url) {
  if (dataUriCache.has(url)) return dataUriCache.get(url);

  const promise = (async () => {
    const res = await fetch(url);
    if (!res.ok) {
      throw new Error(`Failed to fetch font ${url}: ${res.status}`);
    }
    const buffer = await res.arrayBuffer();
    const bytes = new Uint8Array(buffer);
    let binary = '';
    const chunkSize = 0x8000;
    for (let i = 0; i < bytes.length; i += chunkSize) {
      binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunkSize));
    }
    const base64 = btoa(binary);
    return `data:${fontMimeForUrl(url)};base64,${base64}`;
  })();

  // Cache the in-flight promise to dedupe concurrent requests, but drop it on
  // failure so a transient error doesn't permanently poison the cache.
  dataUriCache.set(url, promise);
  promise.catch(() => dataUriCache.delete(url));
  return promise;
}

/** Collects every same-origin @font-face rule currently active in the document. */
function collectFontFaceRules() {
  const rules = [];
  if (typeof document === 'undefined') return rules;

  for (const sheet of Array.from(document.styleSheets)) {
    let cssRules;
    try {
      // Accessing cssRules on a cross-origin stylesheet throws; skip those.
      cssRules = sheet.cssRules;
    } catch {
      continue;
    }
    if (!cssRules) continue;
    for (const rule of Array.from(cssRules)) {
      if (rule.type === 5 /* CSSRule.FONT_FACE_RULE */) {
        rules.push(rule);
      }
    }
  }
  return rules;
}

/**
 * Build embeddable @font-face CSS for the given family names. Every weight/style
 * variant of a matched family is embedded so bold titles, italics, etc. render
 * correctly. Families that cannot be resolved are skipped silently (the export
 * still succeeds, just falling back for that family).
 *
 * @param {Iterable<string>} familyNames - family names or font stacks to embed.
 * @returns {Promise<string>} concatenated @font-face rules, or '' if none.
 */
export async function buildEmbeddedFontFaceCss(familyNames) {
  if (typeof document === 'undefined') return '';

  const wanted = new Set(
    Array.from(familyNames || [])
      .map(firstFamilyToken)
      .filter(Boolean)
      .map((name) => name.toLowerCase())
  );
  if (!wanted.size) return '';

  const matchedRules = collectFontFaceRules().filter((rule) => {
    const family = stripQuotes(rule.style.getPropertyValue('font-family')).toLowerCase();
    return wanted.has(family);
  });

  const blocks = await Promise.all(
    matchedRules.map(async (rule) => {
      const src = rule.style.getPropertyValue('src');
      const url = extractBestUrl(src);
      if (!url) return '';

      let absoluteUrl = url;
      try {
        absoluteUrl = new URL(url, document.baseURI).href;
      } catch {
        /* keep original url */
      }

      let dataUri;
      try {
        dataUri = await fetchAsDataUri(absoluteUrl);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[embedFontsForExport] could not embed font', absoluteUrl, err);
        return '';
      }

      const family = rule.style.getPropertyValue('font-family');
      const weight = rule.style.getPropertyValue('font-weight') || '400';
      const style = rule.style.getPropertyValue('font-style') || 'normal';
      const format = fontFormatForUrl(url);

      return `@font-face{font-family:${family};font-style:${style};font-weight:${weight};font-display:swap;src:url(${dataUri}) format('${format}');}`;
    })
  );

  return blocks.filter(Boolean).join('\n');
}

/**
 * Gathers the distinct font families used by a chart so the exporter knows
 * which @font-face rules to embed. Pulls from the theme's font roles, the
 * extracted title's computed font, and every font-family attribute present on
 * the live SVG's <text>/<tspan> nodes.
 */
export function collectChartFontFamilies(svgEl, theme = {}, titleInfo = null) {
  const families = new Set();
  const add = (value) => {
    const token = firstFamilyToken(value);
    if (token) families.add(token);
  };

  add(theme.fontBody);
  add(theme.fontHeading);
  add(theme.fontLabel);
  add(theme.fontNumber);

  if (titleInfo?.fontFamily) {
    // A computed font-family is a full stack; embed each concrete token.
    String(titleInfo.fontFamily)
      .split(',')
      .forEach((part) => add(part));
  }

  if (svgEl) {
    svgEl.querySelectorAll('text, tspan').forEach((node) => {
      const attr = node.getAttribute('font-family');
      if (attr) {
        attr.split(',').forEach((part) => add(part));
      }
    });
  }

  return Array.from(families);
}
